'use client';

import { useAppSelector } from '@/lib/redux/hooks';
import {
  selectLaunchPromo,
  selectPlans,
} from '@/lib/redux/selectors/pricingSelectors';
import type { CreditPack } from '@/types/pricing';
import { scrollToWhenPresent } from '@/utils/scrollToWhenPresent';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { AudienceSwitcher, type PricingAudience } from './AudienceSwitcher';
import { ClaimProfileFreeCard } from './ClaimProfileFreeCard';
import { OrgPricingSection } from './OrgPricingSection';
import { PackPurchaseSection } from './PackPurchaseSection';
import { type PricingMode, PricingModeToggle } from './PricingModeToggle';
import { PricingV2Section } from './PricingV2Section';
import { ProfessionalPricingSection } from './ProfessionalPricingSection';
import { VatControls } from './VatControls';

interface PricingSwitcherProps {
  /**
   * Un-awaited promise from the Server Component. The Client Component
   * unwraps it with React 19's `use()` hook inside a Suspense boundary.
   * This streams the data from server to client without leaking the
   * backend URL to the browser's Network tab.
   */
  packsPromise: Promise<CreditPack[]>;
}

function isAudience(value: string | null): value is PricingAudience {
  return (
    value === 'individuals' || value === 'solo_practitioner' || value === 'firm'
  );
}

/**
 * Pre-rename `?audience=` values that existing links still carry.
 *
 * The audience segments were renamed lawyers -> solo_practitioner and
 * firms -> firm. `?audience=` has been a public deep link since #5008, so
 * links minted before the rename live in bookmarks, marketing emails and ads
 * we cannot edit. Without this map they fail the `isAudience` guard, the
 * effect below no-ops, and the visitor silently lands on the default
 * Individuals tab — no redirect, no warning, invisible to monitoring, and
 * indistinguishable from `?audience=aliens` to the test suite.
 */
const LEGACY_AUDIENCE_ALIASES: Record<string, PricingAudience> = {
  lawyers: 'solo_practitioner',
  firms: 'firm',
};

/**
 * Resolve a raw `?audience=` value to a current segment, or null.
 *
 * Uses `Object.hasOwn`, NOT the `in` operator. `in` walks the prototype chain,
 * so `?audience=constructor` / `toString` / `hasOwnProperty` would each report
 * present and yield a FUNCTION off `Object.prototype`. That is not merely a
 * wrong tab: React treats a function handed to a state setter as an updater
 * callback, so `setAudience(Object)` would invoke it with the previous state.
 * This value comes straight off the URL, so the input is fully user-supplied.
 */
function resolveAudienceParam(value: string | null): PricingAudience | null {
  if (isAudience(value)) return value;
  if (value !== null && Object.hasOwn(LEGACY_AUDIENCE_ALIASES, value)) {
    // `?? null`: under noUncheckedIndexedAccess an indexed read is
    // `PricingAudience | undefined` even after Object.hasOwn narrows
    // presence, and this function's contract is `| null`.
    return LEGACY_AUDIENCE_ALIASES[value] ?? null;
  }
  return null;
}

/**
 * Pricing surface — MCP-first, audience-driven.
 *
 * The primary decision is the audience (Individuals | Solo Practitioners | Firms);
 * billing cadence lives inside each audience.
 *
 * Layout:
 *   0. AudienceSwitcher (Individuals | Solo Practitioners | Firms & Teams) — governs everything below
 *   - Individuals:     VatControls + PricingModeToggle (Monthly|Annual|Pack) +
 *                      PricingV2Section / PackPurchaseSection
 *   - Solo Practitioners: Monthly|Annual cadence + ProfessionalPricingSection (one
 *                      row: Profil+ + Starter/Pro AI plans) + ClaimProfileFreeCard
 *   - Firms & Teams:   OrgPricingSection (seats + volume discounts + contact)
 */
export function PricingSwitcher({ packsPromise }: PricingSwitcherProps) {
  const t = useTranslations('PricingPage');

  // VAT controls state -- default: SK consumer (no business / no VAT-payer)
  // so first-time visitors see VAT-inclusive retail prices, not B2B net +
  // "+ DPH". Business + VAT-payer can opt in via the toggle.
  const [country, setCountry] = useState('SK');
  const [isBusiness, setIsBusiness] = useState(false);
  const [isVatPayer, setIsVatPayer] = useState(false);

  const [audience, setAudience] = useState<PricingAudience>('individuals');
  const [mode, setMode] = useState<PricingMode>('monthly');

  // Switching to a non-individual audience while Pack is active would leave a
  // dangling pack selection (packs are audience-agnostic AI top-ups that only
  // belong on the Individuals tab). Reset to monthly so the solo_practitioner/firm
  // cadence toggle has a valid Monthly|Annual value.
  const handleAudienceChange = (next: PricingAudience) => {
    if (next !== 'individuals' && mode === 'pack') setMode('monthly');
    setAudience(next);
  };

  // Honor `?scroll=packs` / `#packs` (OverflowPrompt, Usage page) and an
  // optional `?audience=solo_practitioner|firm` deep link. Effect-based to avoid a
  // hydration mismatch.
  //
  // For `#packs`, switching to pack mode renders PackPurchaseSection behind a
  // <Suspense> whose data promise resolves asynchronously — so `#packs` is not
  // in the DOM on the next paint. A rAF-deferred getElementById would fire
  // before the boundary resolves and silently no-op on a cold visit (#5015).
  // `scrollToWhenPresent` watches for the element via a MutationObserver and
  // scrolls once it mounts (with a timeout cap so it never leaks). Its cleanup
  // fn is returned from the effect so React tears the observer down.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hashMatch = window.location.hash === '#packs';
    if (params.get('scroll') === 'packs' || hashMatch) {
      // Packs live only on the Individuals tab.
      setAudience('individuals');
      setMode('pack');
      return hashMatch
        ? scrollToWhenPresent('packs', { behavior: 'smooth' })
        : undefined;
    }
    const resolvedAudience = resolveAudienceParam(params.get('audience'));
    if (resolvedAudience !== null) {
      setAudience(resolvedAudience);
    }
    return undefined;
  }, []);

  // Best annual savings % across individual plans -- drives the Annual badge.
  const reduxPlans = useAppSelector(selectPlans);
  const launchPromo = useAppSelector(selectLaunchPromo);
  const savingsPct = useMemo(() => {
    return reduxPlans
      .filter((p) => p.plan_type === 'individual')
      .reduce<number | null>((best, p) => {
        if (p.annual_price_eur == null || p.price === 0) return best;
        const monthlyTotal = (p.price / 100) * 12;
        const pct = Math.round(
          ((monthlyTotal - p.annual_price_eur) / monthlyTotal) * 100
        );
        return best == null || pct > best ? pct : best;
      }, null);
  }, [reduxPlans]);

  return (
    <div className='w-full space-y-8'>
      {/* 0. Primary audience selector — governs everything below. */}
      <AudienceSwitcher audience={audience} onChange={handleAudienceChange} />

      {/* ---- Individuals ------------------------------------------------- */}
      {audience === 'individuals' && (
        <>
          {/* VAT toggle — only meaningful for subscriptions. Pack prices come
              from the API as raw EUR, so hide the controls in Pack mode to
              avoid the "I set my country to DE but pack prices didn't change"
              confusion. */}
          {mode !== 'pack' && (
            <VatControls
              country={country}
              isBusiness={isBusiness}
              isVatPayer={isVatPayer}
              onCountryChange={setCountry}
              onBusinessChange={setIsBusiness}
              onVatPayerChange={setIsVatPayer}
            />
          )}

          <PricingModeToggle
            mode={mode}
            onChange={setMode}
            savingsPct={savingsPct}
            launchPromo={launchPromo}
          />

          {mode === 'pack' ? (
            <Suspense
              fallback={
                <div className='flex justify-center items-center py-12'>
                  <Loader2 className='h-6 w-6 animate-spin text-content-muted' />
                </div>
              }
            >
              <PackPurchaseSection packsPromise={packsPromise} />
            </Suspense>
          ) : (
            <PricingV2Section
              country={country}
              isBusiness={isBusiness}
              isVatPayer={isVatPayer}
              isAnnual={mode === 'annual'}
              onSwitchToPack={() => setMode('pack')}
            />
          )}

          {/* Cross-sell to the solo practitioner audience. */}
          <div className='flex justify-center'>
            <button
              type='button'
              onClick={() => handleAudienceChange('solo_practitioner')}
              className='inline-flex items-center gap-1.5 text-sm font-medium text-brand-tertiary/80 hover:text-brand-tertiary transition-colors min-h-[44px] px-2'
            >
              {t('lawyerCrossLink')}
              <ArrowRight className='h-3.5 w-3.5' aria-hidden='true' />
            </button>
          </div>
        </>
      )}

      {/* ---- Solo Practitioners ------------------------------------------- */}
      {audience === 'solo_practitioner' && (
        <>
          {/* Lawyers are always a business → VAT-payer toggle only (no Business
              checkbox), country kept for cross-border EU/non-EU practitioners. */}
          <VatControls
            vatPayerOnly
            country={country}
            isBusiness
            isVatPayer={isVatPayer}
            onCountryChange={setCountry}
            onBusinessChange={() => {}}
            onVatPayerChange={setIsVatPayer}
          />

          {/* Monthly|Annual only — packs are audience-agnostic AI top-ups. */}
          <PricingModeToggle
            mode={mode === 'pack' ? 'monthly' : mode}
            onChange={setMode}
            savingsPct={savingsPct}
            launchPromo={launchPromo}
            showPack={false}
          />

          {/* Hero: Profil+ + the AI research plans (Starter/Pro) in one row.
              The AI plans bundle a Profil+ listing free, so they sit directly
              beside the standalone Profil+ tier for comparison. */}
          <ProfessionalPricingSection
            isAnnual={mode === 'annual'}
            country={country}
            isVatPayer={isVatPayer}
          />

          {/* The free path, as text: claiming an existing registry listing
              needs no subscription. Kept below the paid row so it doesn't
              compete for comparison with the SKUs. */}
          <ClaimProfileFreeCard />

          {/* Scope disclosure — which professions are currently covered. */}
          <p className='mx-auto max-w-2xl text-center text-xs text-content-muted'>
            {t('professionalAudienceTooltip')}
          </p>

          {/* Cross-sell back to individual AI plans. */}
          <div className='flex justify-center'>
            <button
              type='button'
              onClick={() => handleAudienceChange('individuals')}
              className='inline-flex items-center gap-1.5 text-sm font-medium text-brand-tertiary/80 hover:text-brand-tertiary transition-colors min-h-[44px] px-2'
            >
              {t('individualCrossLink')}
              <ArrowRight className='h-3.5 w-3.5' aria-hidden='true' />
            </button>
          </div>
        </>
      )}

      {/* ---- Firms & Teams ------------------------------------------------ */}
      {audience === 'firm' && (
        <>
          {/* Firms are always a business → VAT-payer toggle only, country kept. */}
          <VatControls
            vatPayerOnly
            country={country}
            isBusiness
            isVatPayer={isVatPayer}
            onCountryChange={setCountry}
            onBusinessChange={() => {}}
            onVatPayerChange={setIsVatPayer}
          />

          <OrgPricingSection country={country} isVatPayer={isVatPayer} />
        </>
      )}
    </div>
  );
}
